import React, { useState, useEffect } from 'react';
import { checkFirebaseConnectivity, forceNetworkReset } from '../firebase.js';
import { Link } from 'react-router-dom';

/**
 * OfflineIndicator component
 * 
 * Displays an offline/connectivity error indicator
 * Can work in two modes:
 * 1. As a standalone component that checks connectivity
 * 2. As a controlled component with external connectivity state
 */
const OfflineIndicator = ({ isResolving, error, isAuthenticated }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isChecking, setIsChecking] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [connectionError, setConnectionError] = useState(error || null);

  // For controlled mode, if props are provided
  const isControlledMode = isResolving !== undefined;

  // Check connectivity when online status changes (standalone mode only)
  useEffect(() => {
    // Skip if in controlled mode
    if (isControlledMode) return;

    const checkConnection = async () => {
      // Only check Firebase if we're online
      if (navigator.onLine) {
        setIsChecking(true);
        try {
          const result = await checkFirebaseConnectivity();
          setShowBanner(!result.isConnected);
          if (!result.isConnected && result.error) {
            setConnectionError(result.error);
          }
        } catch (error) {
          console.error('Error checking Firebase connectivity:', error);
          setConnectionError(error);
          setShowBanner(true);
        } finally {
          setIsChecking(false);
        }
      } else {
        setShowBanner(true);
      }
    };

    // Check connection on mount and when online status changes
    checkConnection();

    const handleOnline = () => {
      setIsOnline(true);
      checkConnection();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowBanner(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check every 30 seconds while the component is mounted
    const intervalId = setInterval(checkConnection, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [isControlledMode]);

  const handleRetry = async () => {
    if (isResolving || isChecking) return;
    
    setIsChecking(true);
    try {
      if (!navigator.onLine) {
        // Just update the UI if we're offline
        setTimeout(() => setIsChecking(false), 1000);
        return;
      }
      
      const result = await forceNetworkReset();
      if (result.success) {
        setShowBanner(false);
        // Force page refresh if needed after reconnecting
        if (result.shouldRefresh) {
          window.location.reload();
        }
      }
    } catch (error) {
      console.error('Error retrying connection:', error);
      setConnectionError(error);
    } finally {
      setIsChecking(false);
    }
  };

  // In controlled mode, render full-page offline message
  if (isControlledMode) {
    return (
      <div className="offline-container" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <h2>Connection Issue</h2>
        <p>
          {!isOnline 
            ? 'You are currently offline. Please check your internet connection.' 
            : 'Having trouble connecting to StreamerWars. Please wait or try again.'}
        </p>
        
        {connectionError && (
          <div className="error-details" style={{
            margin: '1rem 0',
            padding: '1rem',
            backgroundColor: '#ffebee',
            borderRadius: '4px',
            maxWidth: '100%',
            overflow: 'auto'
          }}>
            <p>{connectionError.message || String(connectionError)}</p>
          </div>
        )}
        
        <div className="action-buttons" style={{
          display: 'flex',
          gap: '1rem',
          marginTop: '2rem'
        }}>
          <button 
            onClick={handleRetry}
            disabled={isResolving}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isResolving ? 'not-allowed' : 'pointer',
              opacity: isResolving ? 0.7 : 1
            }}
          >
            {isResolving ? 'Reconnecting...' : 'Retry Connection'}
          </button>
          
          {isAuthenticated && (
            <Link to="/" style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#f3f4f6',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              textDecoration: 'none'
            }}>
              Go Home
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Standalone mode - return nothing if no connectivity issues
  if (!showBanner) {
    return null;
  }

  // Standalone mode - show banner
  return (
    <div className="offline-banner" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      backgroundColor: isOnline ? '#f59e0b' : '#ef4444',
      color: 'white',
      padding: '0.5rem 1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem'
    }}>
      <span>
        {isOnline
          ? 'Connection to StreamerWars is having issues. Some features may not work.'
          : 'You are offline. Please check your internet connection.'}
      </span>
      
      <button
        onClick={handleRetry}
        disabled={isChecking}
        style={{
          padding: '0.25rem 0.5rem',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: isChecking ? 'not-allowed' : 'pointer'
        }}
      >
        {isChecking ? 'Checking...' : 'Retry'}
      </button>
    </div>
  );
};

export default OfflineIndicator; 